// RoomAmenitiesStrip — compact icon row showing the in-room amenities
// available for a given room (Wi-Fi, AC, minibar, TV, bathroom, shower,
// hairdryer, safe).
//
// - Only renders amenities that the room actually has (via
//   `inferRoomAmenities`).
// - Each icon has a visible label, plus a `title` attribute so hovering
//   surfaces the amenity name on desktop.
// - Wraps gracefully on narrow viewports.

import React from "react";
import { inferRoomAmenities } from "@/lib/roomAmenities";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const RoomAmenitiesStrip = ({ room, lang = "en", testIdPrefix = "room-amenities" }) => {
  const list = inferRoomAmenities(room);
  if (!list.length) return null;

  return (
    <ul
      className="mt-5 flex flex-wrap items-stretch gap-x-3 gap-y-2 text-[11px] text-[#5C5248]"
      data-testid={testIdPrefix}
      aria-label="In-room amenities"
    >
      {list.map((amenity) => {
        const Icon = amenity.icon;
        const label = pick(amenity.label, lang);
        return (
          <li
            key={amenity.id}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#2C2621]/12 bg-[#FDFBF7]/60 hover:border-[#C16542]/50 hover:text-[#2C2621] transition-colors rounded-[2px]"
            title={label}
            data-testid={`${testIdPrefix}-${amenity.id}`}
          >
            <Icon className="w-3.5 h-3.5 text-[#C16542] shrink-0" strokeWidth={1.6} aria-hidden="true" />
            <span className="leading-none">{label}</span>
          </li>
        );
      })}
    </ul>
  );
};

export default RoomAmenitiesStrip;
